import "./utils/axiosAuthInterceptor";
import { createRef, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { isTokenExpired, clearAuthStorage } from "./utils/authToken";
import { ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import { antdTheme } from "./theme/antdTheme";

import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useOutlet,
  useNavigate,
  redirect,
} from "react-router-dom";

import { CSSTransition, SwitchTransition } from "react-transition-group";
import { PermisosProvider } from "./context/PermisosContext";
import RequireModulo from "./components/guards/RequireModulo";
import { actionScroll } from "./redux/actions/utils/utils";
import { actionUserMeGet } from "./redux/actions/login/login";
import Login from "./containers/pages/login";
import Materias from "./containers/pages/materias";
import CrearEmpresas from "./containers/pages/crearEmpresasPage.jsx";
import MateriaLaboral from "./containers/pages/materiaLaboral";
import LaboralConciliacionesPage from "./containers/pages/laboralConciliacionPage.jsx";
import LaboralDesvinculacionesPage from "./containers/pages/laboralDesvinculacionesPage.jsx";
import LaboralTribunalPage from "./containers/pages/laboralTribunalPage.jsx";
import LaboralTribunalDetallePage from "./containers/pages/LaboralTribunalDetallePage.jsx";
import CrearTribunalPage from "./containers/pages/crearTribunalPage.jsx";
import LaboralJuntaConciliacionPage from "./containers/pages/LaboralJuntaConciliacionPage.jsx";
import LaboralJuntaConciliacionDetallePage from "./containers/pages/LaboralJuntaConciliacionDetallePage.jsx";
import CrearJuntaConciliacionPage from "./containers/pages/crearJuntaConciliacionPage.jsx";
import Signup from "./containers/pages/signup";
import CrearExpedientePage from "./containers/pages/crearCentroConciliacionPage.jsx";
import CrearDesvinculacionPage from "./containers/pages/crearDesvinculacionPage.jsx";
import Crear from "./containers/pages/crear.jsx";
import Home from "./containers/pages/home";
import Notificaciones from "./containers/pages/notificaciones"
import NotificacionesDetalles from "./containers/pages/notificaciones_detalles.jsx"
import OpenInApp from "./containers/pages/open.jsx"
import CorreosPage from "./containers/pages/correos.jsx"
import CorreosDetallePage from "./containers/pages/correos_detalles.jsx"
import Empresas from "./containers/pages/empresas.jsx";
import Corresponsales from "./containers/pages/corresponsales.jsx";
import ProgramadorPage from "./containers/pages/ProgramadorPage.jsx";
import MisSolicitudesPage from "./containers/pages/MisSolicitudesPage.jsx";
import NuevaFuncionWizardPage from "./containers/pages/NuevaFuncionWizardPage.jsx";
import CrearCorresponsal from "./containers/pages/crearCorresponsalPage.jsx";
import CorresponsalesDetallePage from "./containers/pages/LaboralCorresponsalesDetallePage.jsx";
import Agenda from "./containers/pages/agenda.jsx";
import ConfiguracionPage from "./containers/pages/ConfiguracionPage.jsx";
import EmpresasDetallePage from "./containers/pages/LaboralEmpresasDetallePage.jsx";
import AbogadosPage from "./containers/pages/AbogadosPage.jsx";
import AbogadoDetallePage from "./containers/pages/AbogadoDetallePage.jsx";
import CrearAbogadoPage from "./containers/pages/CrearAbogadoPage.jsx";
import AutoridadesPage from "./containers/pages/AutoridadesPage.jsx";
import AutoridadDetallePage from "./containers/pages/AutoridadDetallePage.jsx";
import CrearAutoridadPage from "./containers/pages/CrearAutoridadPage.jsx";
import InformesPage from "./containers/pages/InformesPage.jsx";
import UsuariosPage from "./containers/pages/UsuariosPage.jsx";
import CrearUsuarioPage from "./containers/pages/crearUsuarioPage.jsx";
import UsuarioDetallePage from "./containers/pages/UsuarioDetallePage.jsx";
import LaboralDesvinculacionesDetallePage from "./containers/pages/LaboralDesvinculacionesDetallePage.jsx";
import LaboralConciliacionDetallePage from "./containers/pages/LaboralConciliacionDetallePage.jsx";
import NotFound from "./containers/errors/error404";
import "./assets/css/lux_bootstrap/bootstrap.css";
import "./assets/css/lux_brand_colors.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./assets/css/administrador.css";
import "./styles.css";
import "./assets/css/styles.css";
import "./assets/css/utils.css";
import "../src/assets/css/header.css";
import "./assets/css/lux_nav_typography.css";
import "./assets/css/fonts.css";

import store from "./store";
import { Provider, useDispatch } from "react-redux";
import ElectronView from "./electron_view";
import { ToastHost, ConfirmHost, PasteContextMenu } from "./components/ui";

// La app no usa service worker; si el navegador tiene uno instalado de una
// version anterior, intercepta las peticiones (incluyendo GET a la API) y
// devuelve respuestas cacheadas viejas en vez de ir al backend. Se
// desregistra cualquier instancia que haya quedado para que esto no le
// pase a nadie que ya haya visitado la app antes.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
  if (window.caches?.keys) {
    window.caches.keys().then((keys) => keys.forEach((key) => window.caches.delete(key)));
  }
}

const routes = [
  {
    path: "/open",
    value: "open",
    name: "OpenInApp",
    element: <OpenInApp />,
    nodeRef: createRef(),
    className: "OpenInApp",
  },
  {
    path: "/login",
    value: "login",
    name: "Login",
    element: <Login />,
    nodeRef: createRef(),
    className: "Login",
  },
  {
    path: "/agenda",
    value: "agenda",
    name: "Agenda",
    element: <Agenda />,
    nodeRef: createRef(),
    className: "Agenda",
    modulo: "agenda",
  },
   {
    path: "/notificaciones",
    value: "notificaciones",
    name: "Notificaciones",
    element: <Notificaciones />,
    nodeRef: createRef(),
    className: "Notificaciones",
  },
   {
    path: "/notificaciones/:idNotificacion",
    value: "NotificacionesDetalles",
    name: "NotificacionesDetalles",
    element: <NotificacionesDetalles />,
    nodeRef: createRef(),
    className: "NotificacionesDetalles",
  },
  {
    path: "/correos",
    value: "Correos",
    name: "Correos",
    element: <CorreosPage />,
    nodeRef: createRef(),
    className: "Correos",
  },
  {
    path: "/correos/:id",
    value: "CorreosDetalle",
    name: "CorreosDetalle",
    element: <CorreosDetallePage />,
    nodeRef: createRef(),
    className: "CorreosDetalle",
  },
  {
    path: "/empresas",
    value: "empresas",
    name: "Empresas",
    element: <Empresas />,
    nodeRef: createRef(),
    className: "Empresas",
    modulo: "empresas",
  },
  {
    path: "/crear",
    value: "crear",
    name: "Crear",
    element: <Crear />,
    nodeRef: createRef(),
    className: "Crear",
  },
  {
    path: "/materias/laboral",
    value: "materia-laboral-0",
    name: "Materia-Laboral",
    element: <MateriaLaboral />,
    nodeRef: createRef(),
    className: "MateriaLaboral",
  },
  {
    path: "/materias/laboral/centro-conciliacion",
    value: "materia-laboral-conciliaciones",
    name: "Materia-Laboral-Conciliaciones",
    element: <LaboralConciliacionesPage />,
    nodeRef: createRef(),
    className: "MateriaConciliacionesTipo",
    modulo: "conciliacion",
  },

  {
    path: "/materias/laboral/:tipo/crear",
    value: "materia-laboral-tipo-crear",
    name: "Materia-Laboral-Tipo-Crear",
    element: <CrearExpedientePage />,
    nodeRef: createRef(),
    className: "MateriaLaboralTipoCrear",
    modulo: "conciliacion",
    accion: "insertar",
  },
  {
    path: "/materias/laboral/centro-conciliacion/:idExpediente",
    value: "materia-laboral-centro-conciliacion",
    name: "Materia-Laboral-Centro-Conciliacion",
    element: <LaboralConciliacionDetallePage />,
    nodeRef: createRef(),
    className: "MateriaLaboralConciliacion",
    modulo: "conciliacion",
    accion: "consultar",
  },
  {
    path: "/empresas/:idEmpresa",
    value: "empresas-detalle",
    name: "Empresas-Detalle",
    element: <EmpresasDetallePage />,
    nodeRef: createRef(),
    className: "EmpresasDetallePage",
    modulo: "empresas",
    accion: "consultar",
  },
  {
    path: "/empresas/crear",
    value: "empresas-crear",
    name: "Empresas-Crear",
    element: <CrearEmpresas />,
    nodeRef: createRef(),
    className: "EmpresasCrear",
    modulo: "empresas",
    accion: "insertar",
  },
  {
    path: "/empresas/:idEmpresa/editar",
    value: "empresas-editar",
    name: "Empresas-Editar",
    element: <CrearEmpresas />,
    nodeRef: createRef(),
    className: "EmpresasEditar",
    modulo: "empresas",
    accion: "editar",
  },
  {
    path: "/corresponsales",
    value: "corresponsales",
    name: "Corresponsales",
    element: <Corresponsales />,
    nodeRef: createRef(),
    className: "Corresponsales",
    modulo: "corresponsales",
  },
  {
    path: "/corresponsales/crear",
    value: "corresponsales-crear",
    name: "Corresponsales-Crear",
    element: <CrearCorresponsal />,
    nodeRef: createRef(),
    className: "CorresponsalesCrear",
    modulo: "corresponsales",
    accion: "insertar",
  },
  {
    path: "/corresponsales/:idCorresponsal",
    value: "corresponsales-detalle",
    name: "Corresponsales-Detalle",
    element: <CorresponsalesDetallePage />,
    nodeRef: createRef(),
    className: "CorresponsalesDetallePage",
    modulo: "corresponsales",
    accion: "consultar",
  },
  {
    path: "/corresponsales/:idCorresponsal/editar",
    value: "corresponsales-editar",
    name: "Corresponsales-Editar",
    element: <CrearCorresponsal />,
    nodeRef: createRef(),
    className: "CorresponsalesEditar",
    modulo: "corresponsales",
    accion: "editar",
  },
  {
    path: "/programador",
    value: "programador",
    name: "Programador",
    element: <ProgramadorPage />,
    nodeRef: createRef(),
    className: "Programador",
    modulo: "programador",
    accion: "consultar",
  },
  {
    path: "/mis-solicitudes",
    value: "mis-solicitudes",
    name: "MisSolicitudes",
    element: <MisSolicitudesPage />,
    nodeRef: createRef(),
    className: "MisSolicitudes",
  },
  {
    path: "/solicitudes/nueva-funcion",
    value: "nueva-funcion-wizard",
    name: "NuevaFuncionWizard",
    element: <NuevaFuncionWizardPage />,
    nodeRef: createRef(),
    className: "NuevaFuncionWizard",
  },
  {
    path: "/solicitudes/nueva-funcion/:idWizard",
    value: "nueva-funcion-wizard-continuar",
    name: "NuevaFuncionWizardContinuar",
    element: <NuevaFuncionWizardPage />,
    nodeRef: createRef(),
    className: "NuevaFuncionWizard",
  },
   {
    path: "/materias/laboral/desvinculaciones",
    value: "materia-laboral-desvinculaciones",
    name: "Materia-Laboral-Desvinculaciones",
    element: <LaboralDesvinculacionesPage />,
    nodeRef: createRef(),
    className: "MateriaDesvinculacionesTipo",
    modulo: "desvinculaciones",
  },
  {
    path: "/materias/laboral/desvinculaciones/crear",
    value: "materia-laboral-desvinculaciones-crear",
    name: "Materia-Laboral-Desvinculaciones-crear",
    element: <CrearDesvinculacionPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralDesvinculacionesCrear",
    modulo: "desvinculaciones",
    accion: "insertar",
  },
  {
    path: "/materias/laboral/desvinculaciones/:idExpediente",
    value: "materia-laboral-desvinculaciones",
    name: "Materia-Laboral-Desvinculaciones",
    element: <LaboralDesvinculacionesDetallePage />,
    nodeRef: createRef(),
    className: "MateriaLaboralDesvinculaciones",
    modulo: "desvinculaciones",
    accion: "consultar",
  },
  {
    path: "/materias/laboral/desvinculaciones/:idExpediente/editar",
    value: "materia-laboral-desvinculaciones-editar",
    name: "Materia-Laboral-Desvinculaciones-Editar",
    element: <CrearDesvinculacionPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralDesvinculacionesEditar",
    modulo: "desvinculaciones",
    accion: "editar",
  },
  {
    path: "/materias/laboral/tribunal",
    value: "materia-laboral-tribunal",
    name: "Materia-Laboral-Tribunal",
    element: <LaboralTribunalPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralTribunal",
    modulo: "tribunal",
  },
  {
    path: "/materias/laboral/tribunal/crear",
    value: "materia-laboral-tribunal-crear",
    name: "Materia-Laboral-Tribunal-Crear",
    element: <CrearTribunalPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralTribunalCrear",
    modulo: "tribunal",
    accion: "insertar",
  },
  {
    path: "/materias/laboral/tribunal/:idExpediente",
    value: "materia-laboral-tribunal",
    name: "Materia-Laboral-Tribunal",
    element: <LaboralTribunalDetallePage />,
    nodeRef: createRef(),
    className: "MateriaLaboralTribunal",
    modulo: "tribunal",
    accion: "consultar",
  },
  {
    path: "/materias/laboral/tribunal/:idExpediente/editar",
    value: "materia-laboral-tribunal-editar",
    name: "Materia-Laboral-Tribunal-Editar",
    element: <CrearTribunalPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralTribunalEditar",
    modulo: "tribunal",
    accion: "editar",
  },
  {
    path: "/materias/laboral/junta-conciliacion",
    value: "materia-laboral-junta",
    name: "Materia-Laboral-Junta",
    element: <LaboralJuntaConciliacionPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralJunta",
    modulo: "junta",
  },
  {
    path: "/materias/laboral/junta-conciliacion/crear",
    value: "materia-laboral-junta-crear",
    name: "Materia-Laboral-Junta-Crear",
    element: <CrearJuntaConciliacionPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralJuntaCrear",
    modulo: "junta",
    accion: "insertar",
  },
  {
    path: "/materias/laboral/junta-conciliacion/:idJunta",
    value: "materia-laboral-junta-detalle",
    name: "Materia-Laboral-Junta-Detalle",
    element: <LaboralJuntaConciliacionDetallePage />,
    nodeRef: createRef(),
    className: "MateriaLaboralJuntaDetalle",
    modulo: "junta",
    accion: "consultar",
  },
  {
    path: "/materias/laboral/junta-conciliacion/:idJunta/editar",
    value: "materia-laboral-junta-editar",
    name: "Materia-Laboral-Junta-Editar",
    element: <CrearJuntaConciliacionPage />,
    nodeRef: createRef(),
    className: "MateriaLaboralJuntaEditar",
    modulo: "junta",
    accion: "editar",
  },

  {
    path: "/materias/laboral/centro-conciliacion/:idExpediente/editar",
    value: "materia-laboral-centro-conciliacion-editar",
    name: "Materia-Laboral-Centro-Conciliacion-Editar",
    element: <CrearExpedientePage />,
    nodeRef: createRef(),
    className: "MateriaLaboralConciliacionEditar",
    modulo: "conciliacion",
    accion: "editar",
  },
  {
    path: "/materias",
    value: "materias-0",
    name: "Materias",
    element: <Materias />,
    nodeRef: createRef(),
    className: "Materias",
  },
  {
    path: "/signup",
    value: "signup-0",
    name: "Signup",
    element: <Signup />,
    nodeRef: createRef(),
    className: "Signup",
  },
  {
    path: "/web",
    value: "web",
    name: "WebBlock",
    element: (
      <div style={{ padding: 24 }}>
        <h6>Esta aplicación solo está disponible en escritorio.</h6>
      </div>
    ),
    nodeRef: createRef(),
    className: "WebBlock",
  },
  {
    path: "/home",
    value: "0-0",
    name: "Home",
    element: <Home />,
    nodeRef: createRef(),
    className: "Home",
  },
  {
    path: "/configuracion",
    value: "configuracion",
    name: "Configuracion",
    element: <ConfiguracionPage />,
    nodeRef: createRef(),
    className: "Configuracion",
    modulo: "configuracion",
  },
  {
    path: "/abogados",
    value: "abogados",
    name: "Abogados",
    element: <AbogadosPage />,
    nodeRef: createRef(),
    className: "Abogados",
    modulo: "abogados",
  },
  {
    path: "/abogados/crear",
    value: "abogados-crear",
    name: "Abogados-Crear",
    element: <CrearAbogadoPage />,
    nodeRef: createRef(),
    className: "AbogadosCrear",
    modulo: "abogados",
    accion: "insertar",
  },
  {
    path: "/abogados/:idAbogado",
    value: "abogados-detalle",
    name: "Abogados-Detalle",
    element: <AbogadoDetallePage />,
    nodeRef: createRef(),
    className: "AbogadosDetalle",
    modulo: "abogados",
    accion: "consultar",
  },
  {
    path: "/abogados/:idAbogado/editar",
    value: "abogados-editar",
    name: "Abogados-Editar",
    element: <CrearAbogadoPage />,
    nodeRef: createRef(),
    className: "AbogadosEditar",
    modulo: "abogados",
    accion: "editar",
  },
  {
    path: "/autoridades",
    value: "autoridades",
    name: "Autoridades",
    element: <AutoridadesPage />,
    nodeRef: createRef(),
    className: "Autoridades",
    modulo: "autoridades",
  },
  {
    path: "/autoridades/crear",
    value: "autoridades-crear",
    name: "Autoridades-Crear",
    element: <CrearAutoridadPage />,
    nodeRef: createRef(),
    className: "AutoridadesCrear",
    modulo: "autoridades",
    accion: "insertar",
  },
  {
    path: "/autoridades/:idAutoridad",
    value: "autoridades-detalle",
    name: "Autoridades-Detalle",
    element: <AutoridadDetallePage />,
    nodeRef: createRef(),
    className: "AutoridadesDetalle",
    modulo: "autoridades",
    accion: "consultar",
  },
  {
    path: "/informes",
    value: "informes",
    name: "Informes",
    element: <InformesPage />,
    nodeRef: createRef(),
    className: "Informes",
    modulo: "informes",
  },
  {
    path: "/usuarios",
    value: "usuarios",
    name: "Usuarios",
    element: <UsuariosPage />,
    nodeRef: createRef(),
    className: "Usuarios",
    modulo: "usuarios",
  },
  {
    path: "/usuarios/crear",
    value: "usuarios-crear",
    name: "Usuarios-Crear",
    element: <CrearUsuarioPage />,
    nodeRef: createRef(),
    className: "UsuariosCrear",
    modulo: "usuarios",
    accion: "insertar",
  },
  {
    path: "/usuarios/:idUser",
    value: "usuarios-detalle",
    name: "Usuarios-Detalle",
    element: <UsuarioDetallePage />,
    nodeRef: createRef(),
    className: "UsuariosDetalle",
    modulo: "usuarios",
    accion: "consultar",
  },
  {
    path: "*",
    value: "NotFound",
    name: "NotFound",
    element: <NotFound />,
    nodeRef: createRef(),
    className: "NotFound",
  },
];

const isElectron = () => {
  if (typeof window === "undefined") return false;
  return navigator.userAgent.toLowerCase().includes("electron");
};


const requireAuth = ({ request }) => {
  const url = new URL(request.url);
  const path = url.pathname.replace(/\/+$/, "") || "/";

  if (!isElectron() && path !== "/web") {
  //  throw redirect("/web");
  }

  const token = localStorage.getItem("token");

  const publicRoutes = ["/login", "/signup", "/prelogin", "/web", "/open"];
  const isPublic = publicRoutes.includes(path);

  if (isPublic) return null;

  // Se valida tanto la ausencia del token como su expiracion: este loader
  // corre en CADA navegacion (click en el navbar, URL escrita a mano,
  // navigate() programatico), asi que es el punto correcto para forzar el
  // logout sin depender de que algun request a la API falle primero.
  if (!token || isTokenExpired(token)) {
    clearAuthStorage();
    throw redirect("/login");
  }

  return null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Example />,
    loader: requireAuth,
    children: routes.map((route) => ({
      index: route.path === "/",
      path: route.path === "/" ? undefined : route.path,
      element: route.modulo
        ? <RequireModulo modulo={route.modulo} accion={route.accion}>{route.element}</RequireModulo>
        : route.element,
    })),
  },
]);

function Example() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentOutlet = useOutlet();
  const token = localStorage.getItem("token");

  const pathname = location.pathname;
  const normalpath = pathname.replace(/\/+$/, "") || "/";

  // ✅ Hooks SIEMPRE se ejecutan; adentro decides qué hacer
  useEffect(() => {
    if (!isElectron()) return;
    if (token) dispatch(actionUserMeGet());
  }, [dispatch, location.pathname]);

  // ✅ Si estás en WEB: renderiza SOLO el mensaje
  //if (!isElectron()) {
  //  return (
   //   <div style={{ padding: 24 }}>
     //   <h6>Esta aplicación solo está disponible en escritorio.</h6>
     // </div>
   // );
 // }

  // =========================
  // Resolver routecorrect (soporta :tipo, :idExpediente, etc.)
  // =========================
  let routecorrect = routes.find((route) => {
    if (!route?.path) return false;

    if (route.path === "*") return true;
    if (route.path === normalpath) return true;

    const routeParts = route.path.split("/").filter(Boolean);
    const pathParts = normalpath.split("/").filter(Boolean);

    if (routeParts.length !== pathParts.length) return false;

    for (let i = 0; i < routeParts.length; i++) {
      const rp = routeParts[i];
      const pp = pathParts[i];
      if (rp.startsWith(":")) continue;
      if (rp !== pp) return false;
    }
    return true;
  });

  const { nodeRef } = routecorrect || {};
  const routeclass = routecorrect?.value || "unknown";

  const hideUserPopover = ["/login", "/prelogin", "/signup"].includes(normalpath);

  const handleRightClick = (event) => {
    if (event.target.classList.contains("cardcatalogo")) {
      event.preventDefault();
    }
  };

  const onScroll = (event) => {
    dispatch(actionScroll(event.currentTarget.scrollTop));
  };

  // Vista vertical fija para todos los usuarios (sin preferencia por usuario).
  const isVertical = true;

  const canShowUser = token != null && !hideUserPopover;
  const topOffset = isVertical ? 44 : (canShowUser ? 55 : 45);
  const leftOffset = isVertical ? 56 : 0;

  return (
    <>
      <ElectronView
        prelogin={routeclass}
        routecorrect={routecorrect}
        hideUserPopover={hideUserPopover}
      />

      <div
        className="content-electron"
        style={{
          marginTop: topOffset,
          marginLeft: leftOffset,
          height: `calc(100vh - ${topOffset}px)`,
          width: `calc(100vw - ${leftOffset}px)`,
        }}
      >
   
        <div onScroll={onScroll} onContextMenu={handleRightClick}>
          <div>
            <SwitchTransition>
              <CSSTransition
                key={location.pathname}
                nodeRef={nodeRef}
                timeout={200}
                classNames="page"
                unmountOnExit
              >
                {(state) => (
                  <div ref={nodeRef} className="page">
                    {currentOutlet}
                  </div>
                )}
              </CSSTransition>
            </SwitchTransition>
          </div>
        </div>
      </div>
    </>
  );
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <ConfigProvider theme={antdTheme} locale={esES}>
      <PermisosProvider>
        <RouterProvider router={router} />
        <ToastHost />
        <ConfirmHost />
        <PasteContextMenu />
      </PermisosProvider>
    </ConfigProvider>
  </Provider>
);
